import KfhtradeBatchHistory from '../../components/kfhtrade_batch_history';
import KfhtradeBatchUpdatesModal from '../../components/kfhtrade_batch_updates_modal';
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { BsPlusCircle } from "react-icons/bs";

import { ActionIcon, Badge, Button, Card, Checkbox, Grid, Group, SegmentedControl, SimpleGrid, Space, Stack, Text, ThemeIcon, Title, Tooltip, } from "@mantine/core";
import { useLocalStorage, useSessionStorage } from "@mantine/hooks";
import { DatePickerInput } from "@mantine/dates";


import { IconEye, IconTable } from "@tabler/icons-react";

import ServicesInstantSearch from "../base_service/services_instant_search";
import ServicesFirestoreSearch from "../base_service/services_firestore_search";
import { CustomClaims, KYCRiskCode, Service, ServiceLastActionActor, ServiceStatusCode, ServiceType } from "../../types";
import GenerateExecutionServicesCSVModal from "../../components/generate_execution_services_csv_modal";
import GenerateServicesExcelModal from "../../components/generate_services_excel_modal";

import KCCModalButton from "../base_service/KCCModalButton";
import { MyUserDataContext } from "../../contexts";
import { FilterSection } from "../../components/FilterModal";
import KfhtradeKycReviewSummary, { KYC_REVIEW_START_DATE, REVIEW_WINDOW_DAYS } from "../../components/kfhtrade_kyc_review_summary";
import { fromMantineDate } from "../../helpers/mantineDates";





interface ActionsComponentProps {
  serviceObj: Service;
}

type ServicesSearchBackend = "firestore" | "typesense";
const DEFAULT_SERVICES_SEARCH_BACKEND: ServicesSearchBackend =
  import.meta.env.VITE_SERVICES_SEARCH_BACKEND === "firestore" ? "firestore" : "typesense";
const SERVICES_SEARCH_BACKEND_VERSION = "v2";
const MAIN_SERVICE_TYPES: ServiceType[] = ["kfhtrade", "ipo"];
const startOfLocalDaySeconds = (date: Date) => {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return Math.floor(value.getTime() / 1000);
};

const endOfLocalDayExclusiveSeconds = (date: Date) => {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  value.setDate(value.getDate() + 1);
  return Math.floor(value.getTime() / 1000);
};

interface KfhtradeChartFilters {
  /** Show KFH Trade *update* requests (customer already had a kfhtrade_id). */
  selectedKfhtradeUpdate: boolean;
  /** Show KFH Trade *create* (new account) requests — anything that is not an update. */
  selectedKfhtradeCreate: boolean;
  createdFrom: Date | null;
  createdTo: Date | null;
  chartFilterActive: boolean;
  selectedKycStatus: string | null;
  /** Who performed the latest action on the service (see ServiceLastAction). */
  lastActionActors: ServiceLastActionActor[];
  lastActionFrom: Date | null;
  lastActionTo: Date | null;
  /** Customer risk as tagged on the service (tags.riskStatus, stored as `kyc_<risk>`). */
  riskLevels: KYCRiskCode[];
}

const EMPTY_CHART_FILTERS: KfhtradeChartFilters = {
  selectedKfhtradeUpdate: false,
  selectedKfhtradeCreate: false,
  createdFrom: null,
  createdTo: null,
  chartFilterActive: false,
  selectedKycStatus: null,
  lastActionActors: [],
  lastActionFrom: null,
  lastActionTo: null,
  riskLevels: [],
};

const RISK_LEVEL_VALUES = new Set<string>(Object.values(KYCRiskCode));

const LAST_ACTION_ACTOR_OPTIONS: { value: ServiceLastActionActor; label: string }[] = [
  { value: "customer", label: "Customer" },
  { value: "team", label: "KFHC team" },
  { value: "system", label: "System" },
];
const LAST_ACTION_ACTOR_VALUES = new Set<string>(LAST_ACTION_ACTOR_OPTIONS.map((option) => option.value));
const lastActionActorLabel = (actor: ServiceLastActionActor) =>
  LAST_ACTION_ACTOR_OPTIONS.find((option) => option.value === actor)?.label ?? actor;

const KFHTRADE_CHART_FILTERS_STORAGE_KEY = "kfhtrade_services_chart_filters";

// Dates do not survive JSON round-trips, so they are stored as ISO strings.
const serializeChartFilters = (filters: KfhtradeChartFilters) => JSON.stringify({
  ...filters,
  createdFrom: filters.createdFrom ? filters.createdFrom.toISOString() : null,
  createdTo: filters.createdTo ? filters.createdTo.toISOString() : null,
  lastActionFrom: filters.lastActionFrom ? filters.lastActionFrom.toISOString() : null,
  lastActionTo: filters.lastActionTo ? filters.lastActionTo.toISOString() : null,
});

const parseStoredDate = (value: unknown): Date | null => {
  if (typeof value !== "string") return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const deserializeChartFilters = (raw: string | undefined): KfhtradeChartFilters => {
  if (!raw) return EMPTY_CHART_FILTERS;
  try {
    const stored = JSON.parse(raw) ?? {};
    return {
      selectedKfhtradeUpdate: Boolean(stored.selectedKfhtradeUpdate),
      selectedKfhtradeCreate: Boolean(stored.selectedKfhtradeCreate),
      createdFrom: parseStoredDate(stored.createdFrom),
      createdTo: parseStoredDate(stored.createdTo),
      chartFilterActive: Boolean(stored.chartFilterActive),
      selectedKycStatus: typeof stored.selectedKycStatus === "string" ? stored.selectedKycStatus : null,
      lastActionActors: Array.isArray(stored.lastActionActors)
        ? stored.lastActionActors.filter((value: unknown): value is ServiceLastActionActor => typeof value === "string" && LAST_ACTION_ACTOR_VALUES.has(value))
        : [],
      lastActionFrom: parseStoredDate(stored.lastActionFrom),
      lastActionTo: parseStoredDate(stored.lastActionTo),
      riskLevels: Array.isArray(stored.riskLevels)
        ? stored.riskLevels.filter((value: unknown): value is KYCRiskCode => typeof value === "string" && RISK_LEVEL_VALUES.has(value))
        : [],
    };
  } catch (error) {
    console.error("Error reading KFH Trade chart filters from sessionStorage", error);
    return EMPTY_CHART_FILTERS;
  }
};

const RenderActions = ({ serviceObj }: ActionsComponentProps) => {
  // if (serviceObj.created && !(serviceObj.created instanceof Timestamp)) {
  //   serviceObj.created = new Timestamp(serviceObj.created as any, 0);
  // }


  const navigate = useNavigate();

  return <Group>


    <ThemeIcon color="blue" variant="light" onClick={() => {
      navigate("/services/" + serviceObj.id, { state: serviceObj })
    }}><IconEye size={16} /></ThemeIcon>


  </Group>

}
const ServicesList = () => {

  const userContext = useContext(MyUserDataContext);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  let userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims || {};


  const [selectedRows, setSelectedRows] = useState<Service[]>([]);
  const [batchUpdatesOpened, setBatchUpdatesOpened] = useState(false);
  const [batchHistoryOpened, setBatchHistoryOpened] = useState(false);
  const [csvModalOpened, setCsvModalOpened] = useState(false);
  const [excelModalOpened, setExcelModalOpened] = useState(false);
  const [servicesRefreshToken, setServicesRefreshToken] = useState(0);
  const [servicesSearchBackend, setServicesSearchBackend] = useLocalStorage<ServicesSearchBackend>({
    key: "services-search-backend",
    defaultValue: DEFAULT_SERVICES_SEARCH_BACKEND,
    getInitialValueInEffect: false,
  });
  const [servicesSearchBackendVersion, setServicesSearchBackendVersion] = useLocalStorage<string>({
    key: "services-search-backend-version",
    defaultValue: "v1",
    getInitialValueInEffect: false,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const { refreshServicesIn } = location.state || {};

  const handleExternalRefresh = () => {
    setServicesRefreshToken((current) => current + 1);
  };

  useEffect(() => {
    setSelectedRows([]);
  }, [servicesSearchBackend]);

  useEffect(() => {
    if (servicesSearchBackendVersion === SERVICES_SEARCH_BACKEND_VERSION) {
      return;
    }

    setServicesSearchBackend(DEFAULT_SERVICES_SEARCH_BACKEND);
    setServicesSearchBackendVersion(SERVICES_SEARCH_BACKEND_VERSION);
  }, [servicesSearchBackendVersion, setServicesSearchBackend, setServicesSearchBackendVersion]);


  useEffect(() => {
    if (refreshServicesIn) {
      const timer = setTimeout(() => {
        // Action to perform after delay
        console.log('Refreshing services...');
        handleExternalRefresh();
        // Place your code here to refresh services or any other intended action
      }, refreshServicesIn);

      // Clean up the timer when the component unmounts or if the value of refreshServicesIn changes
      return () => clearTimeout(timer);
    }
  }, [refreshServicesIn]); // Dependency array, re-run effect if refreshServicesIn changes


  // Chart/date filters live in sessionStorage so they survive navigating to a
  // service's details and back to this list (they reset when the tab closes).
  const [chartFilters, setChartFilters] = useSessionStorage<KfhtradeChartFilters>({
    key: KFHTRADE_CHART_FILTERS_STORAGE_KEY,
    defaultValue: EMPTY_CHART_FILTERS,
    getInitialValueInEffect: false,
    serialize: serializeChartFilters,
    deserialize: deserializeChartFilters,
  });
  const { selectedKfhtradeUpdate, selectedKfhtradeCreate, createdFrom, createdTo, chartFilterActive, selectedKycStatus, lastActionActors, lastActionFrom, lastActionTo, riskLevels } = chartFilters;
  const updateChartFilters = (patch: Partial<KfhtradeChartFilters>) =>
    setChartFilters((current) => ({ ...(current ?? EMPTY_CHART_FILTERS), ...patch }));
  const setSelectedKfhtradeUpdate = (value: boolean) => updateChartFilters({ selectedKfhtradeUpdate: value });
  const setSelectedKfhtradeCreate = (value: boolean) => updateChartFilters({ selectedKfhtradeCreate: value });
  const clearKfhtradeRequestTypeFilter = () => updateChartFilters({ selectedKfhtradeUpdate: false, selectedKfhtradeCreate: false });
  const clearCreatedDateFilter = () => {
    updateChartFilters({ createdFrom: null, createdTo: null, chartFilterActive: false });
  };
  const clearLastActionFilter = () => {
    updateChartFilters({ lastActionActors: [], lastActionFrom: null, lastActionTo: null });
  };
  const toggleLastActionActor = (actor: ServiceLastActionActor) =>
    updateChartFilters({
      lastActionActors: lastActionActors.includes(actor)
        ? lastActionActors.filter((value) => value !== actor)
        : [...lastActionActors, actor],
    });
  const toggleRiskLevel = (risk: KYCRiskCode) =>
    updateChartFilters({
      riskLevels: riskLevels.includes(risk)
        ? riskLevels.filter((value) => value !== risk)
        : [...riskLevels, risk],
    });
  const kfhtradeFilterSections = useMemo<FilterSection[]>(() => [
    {
      id: "kfhtradeRequestType",
      title: "KFH Trade request type",
      type: "custom",
      selectedCount: (selectedKfhtradeUpdate ? 1 : 0) + (selectedKfhtradeCreate ? 1 : 0),
      onClear: clearKfhtradeRequestTypeFilter,
      renderContent: () => (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Update requests come from customers who already have a KFH Trade account; everything else is a create (new account) request.
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xs" verticalSpacing={6}>
            <Checkbox
              key="isKfhtradeUpdate"
              color="green"
              checked={selectedKfhtradeUpdate}
              onChange={(event) => setSelectedKfhtradeUpdate(event.currentTarget.checked)}
              label="KFH Trade update requests"
            />
            <Checkbox
              key="isKfhtradeCreate"
              color="green"
              checked={selectedKfhtradeCreate}
              onChange={(event) => setSelectedKfhtradeCreate(event.currentTarget.checked)}
              label="KFH Trade create requests"
            />
          </SimpleGrid>
        </Stack>
      ),
    },
    {
      id: "createdDate",
      title: "Filter by Date",
      type: "custom",
      selectedCount: createdFrom || createdTo ? 1 : 0,
      onClear: clearCreatedDateFilter,
      renderContent: () => (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">Filter services by their created date.</Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
            <DatePickerInput
              label="From"
              placeholder="Start date"
              value={createdFrom}
              valueFormat="DD MMM YYYY"
              clearable
              maxDate={createdTo ?? undefined}
              onChange={(value) => {
                updateChartFilters({ createdFrom: fromMantineDate(value), chartFilterActive: false, selectedKycStatus: null });
              }}
            />
            <DatePickerInput
              label="To"
              placeholder="End date"
              value={createdTo}
              valueFormat="DD MMM YYYY"
              clearable
              minDate={createdFrom ?? undefined}
              onChange={(value) => {
                updateChartFilters({ createdTo: fromMantineDate(value), chartFilterActive: false, selectedKycStatus: null });
              }}
            />
          </SimpleGrid>
        </Stack>
      ),
    },
    {
      id: "lastAction",
      title: "Latest action",
      type: "custom",
      selectedCount: lastActionActors.length + (lastActionFrom || lastActionTo ? 1 : 0),
      onClear: clearLastActionFilter,
      renderContent: () => (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">Who performed the latest action on the service, and when.</Text>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xs" verticalSpacing={6}>
            {LAST_ACTION_ACTOR_OPTIONS.map((option) => (
              <Checkbox
                key={option.value}
                color="green"
                checked={lastActionActors.includes(option.value)}
                onChange={() => toggleLastActionActor(option.value)}
                label={option.label}
              />
            ))}
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
            <DatePickerInput
              label="Latest action from"
              placeholder="Start date"
              value={lastActionFrom}
              valueFormat="DD MMM YYYY"
              clearable
              maxDate={lastActionTo ?? undefined}
              onChange={(value) => updateChartFilters({ lastActionFrom: fromMantineDate(value) })}
            />
            <DatePickerInput
              label="Latest action to"
              placeholder="End date"
              value={lastActionTo}
              valueFormat="DD MMM YYYY"
              clearable
              minDate={lastActionFrom ?? undefined}
              onChange={(value) => updateChartFilters({ lastActionTo: fromMantineDate(value) })}
            />
          </SimpleGrid>
        </Stack>
      ),
    },
    {
      id: "riskLevel",
      title: "Risk",
      type: "custom",
      selectedCount: riskLevels.length,
      onClear: () => updateChartFilters({ riskLevels: [] }),
      renderContent: () => (
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xs" verticalSpacing={6}>
          {Object.values(KYCRiskCode).map((risk) => (
            <Checkbox
              key={risk}
              color="green"
              checked={riskLevels.includes(risk)}
              onChange={() => toggleRiskLevel(risk)}
              label={`${risk[0].toUpperCase()}${risk.slice(1)} risk`}
            />
          ))}
        </SimpleGrid>
      ),
    },
  ], [createdFrom, createdTo, selectedKfhtradeUpdate, selectedKfhtradeCreate, lastActionActors, lastActionFrom, lastActionTo, riskLevels]);
  // Ticking both "update" and "create" means every KFH Trade request, so no clause is needed.
  // The list also shows ipo services; scope the request-type filter to kfhtrade so
  // an ipo request is never counted as a KFH Trade create/update request.
  const kfhtradeRequestTypeClauses = selectedKfhtradeUpdate === selectedKfhtradeCreate
    ? []
    : ["serviceType:=kfhtrade", `isKfhtradeUpdate:=${selectedKfhtradeUpdate}`];
  const kfhtradeFilterClauses = useMemo(() => (
    [
      ...kfhtradeRequestTypeClauses,
      ...(createdFrom ? [`created:>=${startOfLocalDaySeconds(createdFrom)}`] : []),
      ...(createdTo ? [`created:<${endOfLocalDayExclusiveSeconds(createdTo)}`] : []),
      ...(chartFilterActive ? [`tags.serviceStatus.code:=${ServiceStatusCode.waiting_for_kyc}`] : []),
      ...(selectedKycStatus ? [`tags.kycStatus.code:=${selectedKycStatus}`] : []),
      ...(lastActionActors.length ? [`lastAction.actor:=[${lastActionActors.join(",")}]`] : []),
      ...(lastActionFrom ? [`lastAction.date:>=${startOfLocalDaySeconds(lastActionFrom)}`] : []),
      ...(lastActionTo ? [`lastAction.date:<${endOfLocalDayExclusiveSeconds(lastActionTo)}`] : []),
      ...(riskLevels.length ? [`tags.riskStatus.code:=[${riskLevels.map((risk) => `kyc_${risk}`).join(",")}]`] : []),
    ]
  ), [chartFilterActive, createdFrom, createdTo, selectedKfhtradeUpdate, selectedKfhtradeCreate, selectedKycStatus, lastActionActors, lastActionFrom, lastActionTo, riskLevels]);

  const handleDaysRemainingSelect = (days: number) => {
    const createdDate = new Date();
    createdDate.setHours(0, 0, 0, 0);
    createdDate.setDate(createdDate.getDate() - (REVIEW_WINDOW_DAYS - days));
    updateChartFilters({
      selectedKfhtradeUpdate: true,
      selectedKfhtradeCreate: false,
      chartFilterActive: true,
      selectedKycStatus: null,
      // The chart only counts requests created from KYC_REVIEW_START_DATE, so the
      // list must never go back further than that (overdue has no other lower bound).
      createdFrom: days === 0 || createdDate < KYC_REVIEW_START_DATE ? KYC_REVIEW_START_DATE : createdDate,
      createdTo: createdDate,
    });
    setServicesSearchBackend("typesense");
    setSelectedRows([]);
  };

  const handleKycStatusSelect = (status: string) => {
    updateChartFilters({
      selectedKfhtradeUpdate: true,
      selectedKfhtradeCreate: false,
      chartFilterActive: true,
      selectedKycStatus: status,
      createdFrom: KYC_REVIEW_START_DATE,
      createdTo: null,
    });
    setServicesSearchBackend("typesense");
    setSelectedRows([]);
  };

  const hasLastActionFilter = lastActionActors.length > 0 || Boolean(lastActionFrom) || Boolean(lastActionTo);
  const activeChartBadges = selectedKfhtradeUpdate || selectedKfhtradeCreate || createdFrom || createdTo || selectedKycStatus || hasLastActionFilter || riskLevels.length ? (
    <Group my="sm">
      {selectedKfhtradeUpdate && <Badge color="green">
        KFH Trade update requests
        <Button variant="subtle" c="white" onClick={() => setSelectedKfhtradeUpdate(false)}>X</Button>
      </Badge>}
      {selectedKfhtradeCreate && <Badge color="green">
        KFH Trade create requests
        <Button variant="subtle" c="white" onClick={() => setSelectedKfhtradeCreate(false)}>X</Button>
      </Badge>}
      {(createdFrom || createdTo) && <Badge color="indigo">
        {`Created date: ${createdFrom ? createdFrom.toLocaleDateString() : "…"} to ${createdTo ? createdTo.toLocaleDateString() : "…"}`}
        <Button variant="subtle" c="white" onClick={clearCreatedDateFilter}>X</Button>
      </Badge>}
      {selectedKycStatus && <Badge color="teal">
        {`KYC status: ${selectedKycStatus.replace(/^kyc_/, "").replace(/_/g, " ")}`}
        <Button variant="subtle" c="white" onClick={() => {
          updateChartFilters({ selectedKycStatus: null, chartFilterActive: false });
        }}>X</Button>
      </Badge>}
      {lastActionActors.map((actor) => <Badge key={actor} color="blue">
        {`Latest action by: ${lastActionActorLabel(actor)}`}
        <Button variant="subtle" c="white" onClick={() => toggleLastActionActor(actor)}>X</Button>
      </Badge>)}
      {(lastActionFrom || lastActionTo) && <Badge color="violet">
        {`Latest action: ${lastActionFrom ? lastActionFrom.toLocaleDateString() : "…"} to ${lastActionTo ? lastActionTo.toLocaleDateString() : "…"}`}
        <Button variant="subtle" c="white" onClick={() => updateChartFilters({ lastActionFrom: null, lastActionTo: null })}>X</Button>
      </Badge>}
      {riskLevels.map((risk) => <Badge key={risk} color={risk === KYCRiskCode.high ? "red" : risk === KYCRiskCode.medium ? "yellow" : "lime"}>
        {`Risk: ${risk}`}
        <Button variant="subtle" c="white" onClick={() => toggleRiskLevel(risk)}>X</Button>
      </Badge>)}
    </Group>
  ) : undefined;

  const refreshButton = (
    <Button onClick={() => setServicesRefreshToken((current) => current + 1)}>
      Refresh
    </Button>
  );

  const searchableServices = servicesSearchBackend === "typesense" ? (
    <ServicesInstantSearch
      key={`typesense-${servicesRefreshToken}`}
      setSelectedRows={setSelectedRows}
      selectable={true}
      selectedRows={selectedRows}
      renderTags={true}
      serviceType={"kfhtrade"}
      customFilters={`serviceType:[${MAIN_SERVICE_TYPES.join(",")}]`}
      navigateDetails={() => { }}
      additionalFilterSections={kfhtradeFilterSections}
      additionalFilterBadges={activeChartBadges}
      filterClauses={kfhtradeFilterClauses}
      showLastActionColumn
      ActionsComponent={RenderActions}
      indexName={import.meta.env.VITE_TYPESENSE_SERVICES_COLLECTION!}
      additionalChildren={refreshButton}
    />
  ) : (
    <ServicesFirestoreSearch
      setSelectedRows={setSelectedRows}
      selectedRows={selectedRows}
      ActionsComponent={RenderActions}
      additionalChildren={refreshButton}
      serviceTypes={MAIN_SERVICE_TYPES}
    />
  );



  return (
    <>
      <Group justify="space-between">
        <Title>Services</Title>
        <Group>



          {(userClaims?.admin || userClaims?.cr) &&
            <><KCCModalButton /><Button color="orange" onClick={() => setBatchUpdatesOpened(true)}>Update no-KYC on DFN</Button><Button variant="light" onClick={() => setBatchHistoryOpened(true)}>Batch history</Button></>
          }


          <Tooltip label="Export Excel" withArrow>
            <ActionIcon
              onClick={() => setExcelModalOpened(true)}
              size="lg"
              variant="filled"
            >
              <IconTable size={16} />
            </ActionIcon>
          </Tooltip>

          <SegmentedControl
            value={servicesSearchBackend}
            onChange={(value) => setServicesSearchBackend(value as ServicesSearchBackend)}
            data={[
              { label: "Firestore", value: "firestore" },
              { label: "Typesense", value: "typesense" },
            ]}
          />

          <Button
            leftSection={<BsPlusCircle />}
            onClick={() => {
              navigate("/services/add_service");
            }}
          >
            {" "}
            Add service{" "}
          </Button>







        </Group>
      </Group>
      <Space h="xl" />
      <KfhtradeKycReviewSummary
        refreshToken={servicesRefreshToken}
        onDaysRemainingSelect={handleDaysRemainingSelect}
        onKycStatusSelect={handleKycStatusSelect}
      />
      <Space h="md" />
      {/* <Card shadow="sm" p="lg" radius="md" withBservice> */}
      <Card shadow="sm" p="lg" radius="md" withBorder>
        <Grid justify="space-between">
          <Grid.Col>

          </Grid.Col>
          <Grid.Col>
            {searchableServices}
          </Grid.Col>
        </Grid>
      </Card>

      <KfhtradeBatchHistory opened={batchHistoryOpened} onClose={() => setBatchHistoryOpened(false)} />
      <KfhtradeBatchUpdatesModal opened={batchUpdatesOpened} onClose={() => setBatchUpdatesOpened(false)} />
      <GenerateExecutionServicesCSVModal
        opened={csvModalOpened}
        setOpened={setCsvModalOpened}
      />
      <GenerateServicesExcelModal
        opened={excelModalOpened}
        setOpened={setExcelModalOpened}
      />

    </>
  );
};


const Services = () => {
  return (
    <main style={{ padding: "1rem 0" }}>
      <ServicesList />

    </main>
  );
};

export default Services;
